import { enP2PMode } from "../classTalk/CodeEnum";

import {
    gotAudioStreamHelper,
    setPblGroupAudioTrackEnabled,
    gotVideoStreamHelper
} from "../HandsUpClient";

import {
    retryLiveReconnect,
    sendUnpublish,
    setVideoResolution,
    unsubscribeHandsUpConsume
} from "../MediaSoupClient";

import {
    mqSmallGroupStartWithPbl,
    mqSmallGroupStopWithPbl
} from "../../modules/live";

/**
 * audio produce 시작을 돕는 helper
 * 
 * @param {MediaStream} stream - 프로듀스할 오디오 스트림
 * @#param {MediaStreamTrack} track - 프로듀스할 오디오 트랙
 * @since 2025-07-08.
 */
export const produceAudioHelper = async (stream/* track */) => {
    console.log("produceAudioHelper called");

    try {
        if (isMediaStream(stream)) {
            console.log("produceAudioHelper - call await gotAudioStreamHelper(stream, true).");
            await gotAudioStreamHelper(stream, true);
            console.log("produceAudioHelper complete.");
        } else {
            console.log("produceAudioHelper - stream is => ", stream);
        }
    } catch (err) {
        console.log("produceAudioHelper - err => ", err);
    }
}

/**
 * Master 오버레이 등 외부 PBL 제어에서 PBL Group audio producer track을 켜고 끈다.
 *
 * @param {Boolean} isEnabled - audio track 활성화 여부
 * @returns {Promise<Boolean>} 상태 변경 성공 여부
 * @since 2026-09-10.
 */
export const setPblGroupAudioTrackEnabledHelper = async (isEnabled) => {
    // 20260910 PBLGroup: 실제 제어는 Pro Frontend HandsUpClient가 수행한다.
    return setPblGroupAudioTrackEnabled(isEnabled);
}

/**
 * video produce 시작을 돕는 helper
 * 
 * @param {MediaStream} stream - 프로듀스할 비디오 스트림
 * @param {Boolean} isCameraStream - 카메라 스트림인지 여부
 * @#param {MediaStreamTrack} track - 프로듀스할 비디오 트랙
 * @#param {Boolean} isHighQualityMode - 스트림을 고화질 해상도로 보내야하는지 여부
 * @since 2025-07-08.
 */
export const produceVideoHelper = async (stream, isCameraStream/* , track, isHighQualityMode */) => {
    console.log("produceVideoHelper called");

    try {
        /**
         * 2025-07-11 ... Pentalk WiFi에서 Pentalk Pro로 보내는 비디오 스트림은
         * 학생 강의 또는 P1P 모드인 것으로 가정하고, 항상 고화질 해상도의 영상으로
         * 보낼 수 있도록 Pbl helper에서는 [ isHighQualityMode = true ]을
         * 임시 고정값으로 설정함 ... by hjkim
         */
        let isHighQualityMode = true;

        if (isMediaStream(stream)) {
            console.log("produceVideoHelper - call await gotVideoStreamHelper(stream, isHighQualityMode, isCameraStream).");
            await gotVideoStreamHelper(stream, isHighQualityMode, isCameraStream);
            console.log("produceVideoHelper complete.");
        } else {
            console.log("produceVideoHelper - stream is => ", stream);
        }
    } catch (err) {
        console.log("produceVideoHelper - err => ", err);
    }
}

/**
 * produce 종료를 돕는 helper
 * 
 * @param {String} mediaKind - 프로듀스를 종료할 미디어 종류 
 * @since 2025-07-23.
 */
export const unpublishHelper = async (mediaKind) => {
    console.log("unpublishHelper - mediaKind => ", mediaKind);
    sendUnpublish(mediaKind);
}

/**
 * MediaStreamTrack인지 확인하는 헬퍼 함수
 * 
 * @param {Object} obj - 검사할 객체
 * @returns {Boolean} MediaStreamTrack 여부
 * @since 2025-07-15.
 */
const isMediaStreamTrack = (obj) => {
    return obj &&
        typeof obj.kind === "string" &&
        typeof obj.readyState === "string" &&
        typeof obj.getSettings === "function" &&
        typeof obj.clone === "function";
}

/**
 * MediaStream인지 확인하는 헬퍼 함수
 * 
 * @param {Object} obj - 검사할 객체
 * @returns {Boolean} MediaStream 여부
 * @since 2025-07-15.
 */
const isMediaStream = (obj) => {
    return obj &&
        typeof obj.id === "string" &&
        typeof obj.active === "boolean" &&
        typeof obj.getAudioTracks === "function" &&
        typeof obj.getVideoTracks === "function" &&
        typeof obj.getTracks === "function" &&
        typeof obj.addTrack === "function" &&
        typeof obj.removeTrack === "function";
}

/**
 * video track Resolution 변경을 돕는 helper
 * 
 * @param {Boolean} isCameraStream - 카메라 스트림인지 여부
 * @#param {Boolean} isHighQualityMode - 스트림을 고화질 해상도로 보내야하는지 여부
 * @#param {Number} p2pMode - 현재 선택된 p2p 모드
 * @returns {Promise<Boolean>}
 * @since 2025-07-08.
 */
export const setVideoResolutionHelper = async (isCameraStream/* , isHighQualityMode, p2pMode */) => {
    console.log("setVideoResolutionHelper called");

    try {
        /**
         * 2025-07-11 ... Pentalk WiFi에서 Pentalk Pro로 보내는 비디오 스트림은
         * 학생 강의 또는 P1P 모드인 것으로 가정하고, 항상 고화질 해상도의 영상으로
         * 보낼 수 있도록 Pbl helper에서는 각각 [ isHighQualityMode = true ],
         * [ p2pMode = enP2PMode.P1P ]을 임시 고정값으로 설정함 ... by hjkim
         */
        let isHighQualityMode = true;
        let p2pMode = enP2PMode.P1P;

        console.log("setVideoResolutionHelper - call await produceAudio(track.clone()).");
        await setVideoResolution(isHighQualityMode, isCameraStream, true, p2pMode);
        console.log("setVideoResolutionHelper complete.");
    } catch (err) {
        console.log("setVideoResolutionHelper - err => ", err);
    }
}

/**
 * Pentalk Pro에서 Consume한 미디어 스트림의 준비가 완료되면
 * Pentalk WiFi에 미디어 스트림, 컨슘 정보 등을 전달합니다.
 * 
 * @param {JSON} consumeData - 컨슘 데이터 정보
 *        - {String} mediaKind - 미디어 스트림 유형
 *        - {String} roomKind - 컨슘한 방 유형 
 *        - {Number} userSeq - 컨슘한 사용자 시퀀스
 *        - {String} userNickname - 컨슘한 사용자 닉네임
 *        - {MediaStream | null} - stream 미디어 스트림
 * @param {JSON} jsonData - JSON 유형의 기타 정보 데이터
 *        - {JSON} data - JSON 유형의 기타 정보 데이터
 * @since 2025-07-11.
 */
export const mediaConsumeReadyHelper = ({ mediaKind, roomKind, userSeq, userNickname, stream }, { data }) => {
    console.log("mediaConsumeReadyHelper called");
    console.log(`mediaKind[${mediaKind}], roomKind[${roomKind}], userSeq[${userSeq}], userNickname[${userNickname}], stream => `, stream);
    console.log("data => ", data);

    const dataObject = {
        mediaKind,
        roomKind,
        userSeq,
        stream
    };

    if (userNickname && userNickname !== "") {
        dataObject.userNickname = userNickname;
    }

    if (data) {
        dataObject.data = data;
    }

    if (window.opener) {
        if (typeof window.opener.mediaConsumeReady === "function") {
            window.opener.mediaConsumeReady(dataObject);
        } else {
            console.log("window.opener.mediaConsumeReady is not a function!!!");
        }
    } else {
        console.log("mediaConsumeReadyHelper - window.opener is => ", window.opener);
    }
}

/**
 * HTML오디오의 소리를 재생합니다. (with stream 듣기)
 * 
 * @param {String} roomKind - 컨슘한 방 유형
 * @param {Number} userSeq - 컨슘한 사용자 시퀀스
 * @param {MediaStream} stream - 대상 audio stream
 * @param {Number} volume - 대상 audio element 볼륨 크기
 * @#param {HTMLAudioElement} audioEl - 대상 audio element
 * @since 2025-07-11.
 */
export const listenAudioConsumeHelper = async (roomKind, userSeq, stream, volume) => {
    return new Promise(async (resolve, reject) => {
        try {
            if (window.opener) {
                if (typeof window.opener.listenAudioConsume === "function") {
                    window.opener.listenAudioConsume(roomKind, userSeq, stream, volume);
                    resolve(true);
                } else {
                    console.log("window.opener.listenAudioConsume is not a function!!!");
                    resolve(false);
                }
            } else {
                console.log("listenAudioConsumeHelper - window.opener is => ", window.opener);
                resolve(false);
            }
        } catch (err) {
            console.log("listenAudioConsumeHelper - err => ", err);
            reject(err);
        }
    });
}

/**
 * HTML오디오의 소리 재생을 멈춥니다.
 * 
 * @#param {HTMLAudioElement} audioEl - 대상 audio element
 * @param {String} roomKind - 컨슘한 방 유형
 * @param {Number} userSeq - 컨슘한 사용자 시퀀스
 * @returns {Promise<Boolean | Error>}
 * @since 2025-07-11.
 */
export const muteAudioConsumeHelper = async (roomKind, userSeq) => {
    return new Promise(async (resolve, reject) => {
        try {
            if (window.opener) {
                if (typeof window.opener.muteAudioConsume === "function") {
                    window.opener.muteAudioConsume(roomKind, userSeq);
                    resolve(true);
                } else {
                    console.log("window.opener.muteAudioConsume is not a function!!!");
                    resolve(false);
                }
            } else {
                console.log("muteAudioConsumeHelper - window.opener is => ", window.opener);
                resolve(false);
            }
        } catch (err) {
            console.log("muteAudioConsumeHelper - err => ", err);
            reject(err);
        }
    });
}

/**
 * HTML오디오의 소리를 재생합니다. (with stream 듣기)
 * 
 * @param {Number} userSeq - 컨슘한 사용자 시퀀스
 * @param {MediaStream} stream - 대상 audio stream
 * @param {Number} volume - 대상 audio element 볼륨 크기
 * @#param {HTMLAudioElement} audioEl - 대상 audio element
 * @since 2025-07-11.
 */
export const listenMicConsumeHelper = async (userSeq, stream, volume) => {
    return new Promise(async (resolve, reject) => {
        try {
            if (window.opener) {
                if (typeof window.opener.listenMicConsume === "function") {
                    window.opener.listenMicConsume(userSeq, stream, volume);
                    resolve(true);
                } else {
                    console.log("window.opener.listenMicConsume is not a function!!!");
                    resolve(false);
                }
            } else {
                console.log("listenMicConsumeHelper - window.opener is => ", window.opener);
                resolve(false);
            }
        } catch (err) {
            console.log("listenMicConsumeHelper - err => ", err);
            reject(err);
        }
    });
}

/**
 * HTML오디오의 소리 재생을 멈춥니다.
 * 
 * @param {Number} userSeq - 컨슘한 사용자 시퀀스
 * @#param {HTMLAudioElement} audioEl - 대상 audio element
 * @returns {Promise<Boolean | Error>}
 * @since 2025-07-11.
 */
export const muteMicConsumeHelper = async (userSeq) => {
    return new Promise(async (resolve, reject) => {
        try {
            if (window.opener) {
                if (typeof window.opener.muteMicConsume === "function") {
                    window.opener.muteMicConsume(userSeq);
                    resolve(true);
                } else {
                    console.log("window.opener.muteMicConsume is not a function!!!");
                    resolve(false);
                }
            } else {
                console.log("muteMicConsumeHelper - window.opener is => ", window.opener);
                resolve(false);
            }
        } catch (err) {
            console.log("muteMicConsumeHelper - err => ", err);
            reject(err);
        }
    });
}

/**
 * Pentalk WiFi에서 Pentalk Pro로 Json 유형의 명령 정보 데이터를
 * 전달하는 경우 호출하는 Helper 메서드
 * 
 * @param {JSON} jsonData - JSON 데이터
 * @since 2025-12-12.
 */
export const rcvJsonDataHelper = async (jsonData) => {
    console.log("rcvJsonDataHelper - jsonData => ", jsonData);
    const { kind } = jsonData;

    let store = window.hiclasstv.store;
    // const { user } = store.getState();
    // const { userNickname } = user;

    if (kind === "startPblSmallGroup") {
        // const { userSeq } = jsonData;
        store.dispatch(mqSmallGroupStartWithPbl(jsonData));
    } else if (kind === "stopPblSmallGroup") {
        /* const { userSeq } = jsonData;
        // stopPblSmallGroup인 경우 해당 멤버 정보 정리 및 unsubscribe & exitConsume 요청해야함 ... by hjkim 20260115
        unsubscribeHandsUpConsume(false, userSeq); */
        store.dispatch(mqSmallGroupStopWithPbl(jsonData));
    }
}

/**
 * 20260929 선생님 영상(live) 수신 연결 상태를 opener(PBL Group master.html)에 알린다.
 * opener에 함수가 없으면(구버전) 아무 것도 하지 않는다.
 * 
 * @param {String} state - reconnecting / failed / connected / ended
 * @param {Number} attempt - 현재 재시도 횟수
 */
export const liveConnectionStateHelper = (state, attempt) => {
    try {
        if (window.opener && typeof window.opener.liveConnectionState === "function") {
            window.opener.liveConnectionState({ state, attempt });
        }
    } catch (err) {
        console.log("liveConnectionStateHelper - err => ", err);
    }
}

/**
 * 20260929 PBL Group master.html의 [다시 연결] 버튼에서 호출한다.
 */
export const retryLiveConnectHelper = () => {
    retryLiveReconnect();
}
